import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { productsAPI, stockAlertsAPI } from '../lib/api';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useAnalytics } from '../context/AnalyticsContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ShoppingCart, ArrowLeft, Check, Minus, Plus, Truck, Shield, RefreshCw, ChevronLeft, ChevronRight, Puzzle, Clock, Pencil, Save, X, Package, MapPin, Camera, Settings, Search, Loader2, Replace, ImageIcon, Heart, BellRing, Wrench } from 'lucide-react';
import { formatPrice, calculateDiscount, cn, getStockStatus, isProductAvailable, STOCK_STATUSES } from '../lib/utils';
import { ProductCard } from '../components/products/ProductCard';
import { ReviewSection } from '../components/ReviewSection';
import { imageAPI } from '../lib/api';
import { toast } from 'sonner';
import { getRecentlyViewed, addToRecentlyViewed } from '../lib/recentlyViewed';
import { usePageMeta } from '../lib/usePageMeta';
import { useWishlist } from '../context/WishlistContext';
import { categoryUrl } from '../lib/urlHelpers';

var BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
function proxyUrl(url) {
    if (!url || url.startsWith('data:')) return url;
    return BACKEND_URL + '/api/images/proxy?url=' + encodeURIComponent(url);
}

var categoryNames = { 
    gaming_pc: 'Gamer PC-k', 
    office_pc: 'Irodai PC-k', 
    workstation: 'Munkaállomások', 
    component: 'Alkatrészek',
    mobile_phone: 'Mobiltelefonok',
    accessory: 'Tartozékok'
};

export default function ProductDetailPage() {
    var params = useParams();
    var navigate = useNavigate();
    var cartContext = useCart();
    var authContext = useAuth();
    var { trackProductView, trackAddToCart } = useAnalytics();
    var { isInWishlist, toggleWishlist } = useWishlist();
    var [product, setProduct] = useState(null);
    var [loading, setLoading] = useState(true);
    var [quantity, setQuantity] = useState(1);
    var [adding, setAdding] = useState(false);
    var [selectedImageIndex, setSelectedImageIndex] = useState(0);
    var [relatedProducts, setRelatedProducts] = useState([]);
    var [loadingRelated, setLoadingRelated] = useState(false);
    var [recentlyViewed, setRecentlyViewed] = useState([]);
    usePageMeta(product ? product.name : 'Termék betöltése...', product ? product.description?.slice(0, 155) : undefined);
    
    // Admin edit state
    var [editorOpen, setEditorOpen] = useState(false);
    var [editorTab, setEditorTab] = useState('details');
    var [editForm, setEditForm] = useState({});
    var [saving, setSaving] = useState(false);
    // Image editor state
    var [imgSearchQuery, setImgSearchQuery] = useState('');
    var [imgSearchResults, setImgSearchResults] = useState([]);
    var [imgSearching, setImgSearching] = useState(false);
    var [imgSelected, setImgSelected] = useState(new Set());
    var [currentImages, setCurrentImages] = useState([]);
    var [stockAlertSubscribed, setStockAlertSubscribed] = useState(false);
    var [subscribingAlert, setSubscribingAlert] = useState(false);

    useEffect(function() {
        setLoading(true);
        setSelectedImageIndex(0);
        var slug = params.slug;
        // Load recently viewed (excluding current product)
        var viewed = getRecentlyViewed().filter(function(p) { return p.slug !== slug && p.id !== slug; });
        setRecentlyViewed(viewed);
        
        productsAPI.getById(slug)
            .then(function(res) { 
                setProduct(res.data); 
                setLoading(false);
                // Track product view
                trackProductView(res.data.id, res.data.category);
                // Add to recently viewed
                addToRecentlyViewed(res.data);
                // Fetch related products
                setLoadingRelated(true);
                productsAPI.getRelated(res.data.id, 8)
                    .then(function(relRes) { 
                        setRelatedProducts(relRes.data); 
                        setLoadingRelated(false);
                    })
                    .catch(function() { setLoadingRelated(false); });
            })
            .catch(function() { setLoading(false); });
    }, [params.slug, trackProductView]);

    // Check stock alert subscription
    useEffect(function() {
        if (product && !isProductAvailable(product) && authContext.user) {
            stockAlertsAPI.check(product.id)
                .then(function(res) { setStockAlertSubscribed(res.data.subscribed); })
                .catch(function() {});
        }
    }, [product, authContext.user]);

    function handleStockAlert() {
        if (!authContext.user) {
            toast.error('Jelentkezz be az értesítéshez!');
            return;
        }
        setSubscribingAlert(true);
        if (stockAlertSubscribed) {
            stockAlertsAPI.unsubscribe(product.id)
                .then(function() { setStockAlertSubscribed(false); toast.success('Értesítés lemondva'); })
                .catch(function() { toast.error('Hiba történt'); })
                .finally(function() { setSubscribingAlert(false); });
        } else {
            stockAlertsAPI.subscribe(product.id)
                .then(function() { setStockAlertSubscribed(true); toast.success('Értesítünk, ha elérhető lesz!'); })
                .catch(function() { toast.error('Hiba történt'); })
                .finally(function() { setSubscribingAlert(false); });
        }
    }

    function handleAddToCart() {
        setAdding(true);
        // Track add to cart
        trackAddToCart(product.id, product.price * quantity);
        cartContext.addToCart(product.id, quantity).then(function() { setAdding(false); });
    }
    
    function openEditor(tab) {
        setEditForm({
            name: product.name || '',
            description: product.description || '',
            price: product.price || 0,
            original_price: product.original_price || '',
            brand: product.brand || '',
            category: product.category || 'component',
            subcategory: product.subcategory || '',
            condition: product.condition || 'new',
            stock: product.stock || 0,
            stock_status: product.stock_status || (product.stock > 0 ? 'raktaron' : 'nincs_keszleten'),
            featured: product.featured || false,
            visible: product.visible !== false,
        });
        setCurrentImages(product.images || []);
        var query = (product.brand || '') + ' ' + (product.name || '');
        setImgSearchQuery(query.trim());
        setImgSearchResults([]);
        setImgSelected(new Set());
        setEditorTab(tab || 'details');
        setEditorOpen(true);
        if (tab === 'images' && query.trim()) {
            doImageSearch(query.trim());
        }
    }
    
    async function doImageSearch(query) {
        if (!query?.trim()) return;
        setImgSearching(true);
        setImgSearchResults([]);
        try {
            var res = await imageAPI.search(query, 12);
            setImgSearchResults(res.data.images || []);
            if (!res.data.images?.length) toast.info('Nem találtunk képeket');
        } catch { toast.error('Hiba a képek keresésekor'); }
        setImgSearching(false);
    }
    
    function toggleImgSelect(url) {
        setImgSelected(function(prev) {
            var next = new Set(prev);
            if (next.has(url)) next.delete(url); else next.add(url);
            return next;
        });
    }
    
    async function handleReplaceImages() {
        if (imgSelected.size === 0) return;
        setSaving(true);
        try {
            var newImages = Array.from(imgSelected);
            var resp = await productsAPI.updateImages(product.id, newImages);
            if (resp.data.success) {
                var updated = resp.data.images || newImages;
                setCurrentImages(updated);
                setProduct(function(prev) { return { ...prev, images: updated }; });
                setSelectedImageIndex(0);
                setImgSelected(new Set());
                toast.success(updated.length + ' kép beállítva');
            }
        } catch { toast.error('Hiba a képek mentésekor'); }
        setSaving(false);
    }
    
    async function handleAddImages() {
        if (imgSelected.size === 0) return;
        setSaving(true);
        try {
            var merged = [...currentImages];
            imgSelected.forEach(function(url) { if (!merged.includes(url)) merged.push(url); });
            var resp = await productsAPI.updateImages(product.id, merged);
            if (resp.data.success) {
                var updated = resp.data.images || merged;
                setCurrentImages(updated);
                setProduct(function(prev) { return { ...prev, images: updated }; });
                setImgSelected(new Set());
                toast.success('Képek hozzáadva');
            }
        } catch { toast.error('Hiba a képek mentésekor'); }
        setSaving(false);
    }
    
    async function handleRemoveImage(idx) {
        var next = [...currentImages];
        next.splice(idx, 1);
        try {
            var resp = await productsAPI.updateImages(product.id, next);
            if (resp.data.success) {
                var updated = resp.data.images || next;
                setCurrentImages(updated);
                setProduct(function(prev) { return { ...prev, images: updated }; });
                toast.success('Kép eltávolítva');
            }
        } catch { toast.error('Hiba a kép eltávolításakor'); }
    }
    
    function handleEditFormChange(field, value) {
        setEditForm(function(prev) {
            var updated = { ...prev };
            updated[field] = value;
            return updated;
        });
    }
    
    async function handleSaveProduct() {
        setSaving(true);
        try {
            var updateData = {
                ...editForm,
                price: Number(editForm.price),
                original_price: editForm.original_price ? Number(editForm.original_price) : null,
                stock: Number(editForm.stock),
                stock_status: editForm.stock_status || 'raktaron',
            };
            await productsAPI.update(product.id, updateData);
            // Refresh product data
            var res = await productsAPI.getById(product.id);
            setProduct(res.data);
            setEditorOpen(false);
            toast.success('Termék sikeresen frissítve!');
        } catch (error) {
            console.error('Error updating product:', error);
            toast.error('Hiba történt a mentés során');
        } finally {
            setSaving(false);
        }
    }

    var discount = product ? calculateDiscount(product.original_price, product.price) : 0;
    var saved = product ? isInWishlist(product.id) : false;

    if (loading) {
        return (
            <div className="min-h-screen">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                        <div className="aspect-square skeleton rounded-xl" />
                        <div className="space-y-4">
                            <div className="h-8 skeleton rounded w-1/4" />
                            <div className="h-10 skeleton rounded w-3/4" />
                            <div className="h-24 skeleton rounded" />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!product) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <h2 className="font-heading text-2xl font-bold mb-4">Termék Nem Található</h2>
                    <Button onClick={function() { navigate('/products'); }}>
                        <ArrowLeft className="mr-2 w-4 h-4" />Vissza a Termékekhez
                    </Button>
                </div>
            </div>
        );
    }

    // Get all valid images
    var images = (product.images && product.images.length > 0) 
        ? product.images.filter(function(img) { return img && img.length > 0; })
        : ['/placeholder_product.jpg'];
    
    var currentImage = images[selectedImageIndex] || images[0];

    // Get specs as array for 2-column display, filtering out price-related entries
    var priceRelatedKeys = ['ár', 'ar', 'price', 'legolcsóbb', 'legolcsobb', 'minimum_ár', 'minimum_ar', 'ft', 'huf', 'kedvezmény', 'kedvezmeny', 'akció', 'akcio', 'eredeti_ár', 'eredeti_ar'];
    var specsEntries = Object.entries(product.specs || {}).filter(function(entry) {
        var key = entry[0].toLowerCase();
        var value = String(entry[1]).toLowerCase();
        // Filter out if key contains price-related words
        if (priceRelatedKeys.some(function(priceKey) { return key.includes(priceKey); })) {
            return false;
        }
        // Filter out if value looks like a price (number followed by Ft or HUF)
        if (/^\s*[\d\s]+\s*(ft|huf)\s*$/i.test(value)) {
            return false;
        }
        // Filter out if value is just a number that looks like a price (> 1000)
        var numValue = parseInt(value.replace(/\s/g, ''), 10);
        if (!isNaN(numValue) && numValue > 10000 && /^\s*[\d\s]+\s*$/.test(value)) {
            return false;
        }
        return true;
    });
    var halfLength = Math.ceil(specsEntries.length / 2);
    var specsCol1 = specsEntries.slice(0, halfLength);
    var specsCol2 = specsEntries.slice(halfLength);

    return (
        <div className="min-h-screen" data-testid="product-detail-page">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-8 flex-wrap">
                    <Link to="/" className="hover:text-foreground transition-colors">Főoldal</Link>
                    <span>/</span>
                    <Link to="/products" className="hover:text-foreground transition-colors">Termékek</Link>
                    <span>/</span>
                    <Link to={categoryUrl(product.category)} className="hover:text-foreground transition-colors">
                        {categoryNames[product.category] || product.category}
                    </Link>
                    <span>/</span>
                    <span className="text-foreground">{product.name}</span>
                </nav>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                    {/* Image Gallery */}
                    <div className="space-y-4">
                        <div className="relative rounded-xl overflow-hidden bg-card border border-border" style={{height: '550px'}}>
                            <img src={proxyUrl(currentImage)} alt={product.name} className="w-full h-full object-contain" />
                            <div className="absolute top-4 left-4 flex flex-col gap-2">
                                <Badge className={cn("text-sm", product.condition === 'new' ? "bg-cyan-500 hover:bg-cyan-600" : "bg-amber-500 hover:bg-amber-600")}>
                                    {product.condition === 'new' ? 'Új' : 'Felújított'}
                                </Badge>
                                {discount > 0 && <Badge className="bg-red-500 hover:bg-red-600 text-sm">-{discount}%</Badge>}
                            </div>
                            
                            {/* Image Navigation Arrows */}
                            {images.length > 1 && (
                                <>
                                    <button 
                                        onClick={function() { setSelectedImageIndex(selectedImageIndex > 0 ? selectedImageIndex - 1 : images.length - 1); }}
                                        className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-colors"
                                    >
                                        <ChevronLeft className="w-6 h-6" />
                                    </button>
                                    <button 
                                        onClick={function() { setSelectedImageIndex(selectedImageIndex < images.length - 1 ? selectedImageIndex + 1 : 0); }}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-colors"
                                    >
                                        <ChevronRight className="w-6 h-6" />
                                    </button>
                                </>
                            )}
                        </div>
                        
                        {/* Thumbnail Selector */}
                        {images.length > 1 && (
                            <div className="flex gap-2 overflow-x-auto pb-2">
                                {images.map(function(img, index) {
                                    return (
                                        <button
                                            key={index}
                                            onClick={function() { setSelectedImageIndex(index); }}
                                            className={cn(
                                                "flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all",
                                                selectedImageIndex === index 
                                                    ? "border-primary ring-2 ring-primary/20" 
                                                    : "border-border hover:border-primary/50"
                                            )}
                                        >
                                            <img src={proxyUrl(img)} alt={product.name + ' ' + (index + 1)} className="w-full h-full object-cover" />
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Product Info */}
                    <div>
                        <p className="text-sm text-muted-foreground mb-2">{product.brand}</p>
                        <div className="flex items-start justify-between gap-4 mb-4">
                            <h1 className="font-heading text-3xl font-bold">{product.name}</h1>
                            {authContext.isAdmin && (
                                <div className="flex gap-2 flex-shrink-0">
                                    <Button 
                                        variant="outline" 
                                        size="sm" 
                                        onClick={() => openEditor('images')}
                                        data-testid="admin-photo-button"
                                        title="Képek szerkesztése"
                                    >
                                        <Camera className="w-4 h-4 mr-2" />
                                        Képek
                                    </Button>
                                    <Button 
                                        variant="outline" 
                                        size="sm" 
                                        onClick={() => openEditor('details')}
                                        data-testid="admin-edit-button"
                                    >
                                        <Pencil className="w-4 h-4 mr-2" />
                                        Szerkesztés
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => navigate(`/admin?tab=products&editProduct=${product.id}`)}
                                        data-testid="admin-panel-button"
                                        title="Admin panel"
                                    >
                                        <Settings className="w-4 h-4" />
                                    </Button>
                                </div>
                            )}
                        </div>
                        
                        <div className="flex items-baseline gap-3 mb-4">
                            <span className="font-mono text-4xl font-bold text-primary">{formatPrice(product.price)}</span>
                            {product.original_price && product.original_price > product.price && (
                                <span className="font-mono text-xl text-muted-foreground line-through">{formatPrice(product.original_price)}</span>
                            )}
                        </div>

                        <div className="flex items-center gap-2 mb-6">
                            <div className={cn("w-2 h-2 rounded-full", getStockStatus(product).dotColor)} />
                            <span className={cn("text-sm font-medium", getStockStatus(product).color)}>
                                {getStockStatus(product).label}
                            </span>
                            {getStockStatus(product).delivery && (
                                <span className="text-xs text-muted-foreground ml-1">- {getStockStatus(product).delivery}</span>
                            )}
                        </div>

                        {product.description && <p className="text-muted-foreground mb-8">{product.description}</p>}

                        <div className="flex items-center gap-4 mb-8">
                            {isProductAvailable(product) ? (
                                <>
                                    <div className="flex items-center border border-border rounded-md">
                                        <button onClick={function() { setQuantity(Math.max(1, quantity - 1)); }} className="p-3 hover:bg-secondary transition-colors" disabled={quantity <= 1}>
                                            <Minus className="w-4 h-4" />
                                        </button>
                                        <span className="w-12 text-center font-mono">{quantity}</span>
                                        <button onClick={function() { setQuantity(Math.min(product.stock, quantity + 1)); }} className="p-3 hover:bg-secondary transition-colors" disabled={quantity >= product.stock}>
                                            <Plus className="w-4 h-4" />
                                        </button>
                                    </div>
                                    <Button size="lg" className="flex-1 btn-glow" onClick={handleAddToCart} disabled={adding} data-testid="add-to-cart-button">
                                        {adding ? 'Hozzáadás...' : <><ShoppingCart className="w-5 h-5 mr-2" />Kosárba</>}
                                    </Button>
                                </>
                            ) : (
                                <Button
                                    size="lg"
                                    variant={stockAlertSubscribed ? "secondary" : "default"}
                                    className={cn("flex-1", stockAlertSubscribed ? "border-green-500/30 bg-green-500/10 text-green-500 hover:bg-green-500/20" : "bg-amber-500 hover:bg-amber-600")}
                                    onClick={handleStockAlert}
                                    disabled={subscribingAlert}
                                    data-testid="stock-alert-button"
                                >
                                    {subscribingAlert ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : stockAlertSubscribed ? <Check className="w-5 h-5 mr-2" /> : <BellRing className="w-5 h-5 mr-2" />}
                                    {stockAlertSubscribed ? 'Értesítés bekapcsolva' : 'Értesíts, ha elérhető!'}
                                </Button>
                            )}
                            <Button
                                size="lg"
                                variant="outline"
                                onClick={function() { toggleWishlist(product.id); }}
                                className={cn("flex-shrink-0 transition-all", saved && "border-red-500/30 bg-red-500/5 hover:bg-red-500/10")}
                                data-testid="wishlist-button"
                                title={saved ? 'Eltávolítás a kedvencekből' : 'Hozzáadás a kedvencekhez'}
                            >
                                <Heart className={cn("w-5 h-5", saved ? "fill-red-500 text-red-500" : "text-muted-foreground")} />
                            </Button>
                        </div>

                        {/* Customize in PC Builder - only for PC categories */}
                        {['gaming_pc', 'office_pc', 'workstation'].includes(product.category) && Object.keys(product.specs || {}).length > 0 && (
                            <Button 
                                variant="outline" 
                                size="lg" 
                                className="w-full mb-8 bg-primary/10 border-primary/40 text-primary hover:bg-primary/20 font-medium" 
                                onClick={() => navigate(`/pc-builder?base=${product.id}`)}
                                data-testid="customize-pc-button"
                            >
                                <Wrench className="w-5 h-5 mr-2" />
                                Testreszabás
                            </Button>
                        )}
                    </div>
                </div>

                {/* Full-width Specifications Section */}
                {(specsEntries.length > 0 || product.specifications?.passmark_score) && (
                    <div className="mt-10 bg-card rounded-xl border border-border p-6" data-testid="specs-section">
                        <h3 className="font-heading text-xl font-bold mb-6 flex items-center gap-2">
                            <Settings className="w-5 h-5 text-primary" />
                            Specifikációk
                        </h3>
                        {['gaming_pc', 'office_pc', 'workstation'].includes(product.category) ? (
                            <div className="space-y-0">
                                {specsEntries.map(function(entry) {
                                    var key = entry[0];
                                    var value = entry[1];
                                    return (
                                        <div key={key} className="flex justify-between items-start py-3 border-b border-border gap-8">
                                            <span className="text-muted-foreground capitalize text-sm font-medium whitespace-nowrap">{key.replace(/_/g, ' ')}</span>
                                            <span className="text-sm text-right">{typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8">
                            {product.specifications?.passmark_score && (product.subcategory === 'cpu' || product.subcategory === 'gpu') && (
                                <div className="flex justify-between py-2.5 border-b border-border">
                                    <span className="text-muted-foreground text-sm">PassMark Score</span>
                                    <span className="font-mono text-sm text-right ml-4 text-primary font-bold">{product.specifications.passmark_score.toLocaleString()}</span>
                                </div>
                            )}
                            {specsEntries.map(function(entry) {
                                var key = entry[0];
                                var value = entry[1];
                                return (
                                    <div key={key} className="flex justify-between py-2.5 border-b border-border">
                                        <span className="text-muted-foreground capitalize text-sm">{key.replace(/_/g, ' ')}</span>
                                        <span className="font-mono text-sm text-right ml-4">{typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>
                                    </div>
                                );
                            })}
                        </div>
                        )}
                    </div>
                )}

                {/* 3-column grid: Szállítás, Fontos tudnivalók, Termék Információ */}
                <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Column 1: Szállítás */}
                    <div className="bg-card rounded-xl border border-border p-6" data-testid="shipping-info">
                        <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                            <Truck className="w-5 h-5 text-primary" />
                            Szállítás
                        </h3>
                        <div className="space-y-0">
                            <div className="flex items-center justify-between py-2.5 border-b border-border">
                                <div className="flex items-center gap-2">
                                    <Truck className="w-4 h-4 text-muted-foreground" />
                                    <span className="text-sm">GLS Futárszolgálat</span>
                                </div>
                                <span className="text-sm font-mono">1 490 Ft</span>
                            </div>
                            <div className="flex items-center justify-between py-2.5 border-b border-border">
                                <div className="flex items-center gap-2">
                                    <Truck className="w-4 h-4 text-muted-foreground" />
                                    <span className="text-sm">MPL Csomag</span>
                                </div>
                                <span className="text-sm font-mono">1 290 Ft</span>
                            </div>
                            <div className="flex items-center justify-between py-2.5 border-b border-border">
                                <div className="flex items-center gap-2">
                                    <Package className="w-4 h-4 text-muted-foreground" />
                                    <span className="text-sm">Foxpost Automata</span>
                                </div>
                                <span className="text-sm font-mono">990 Ft</span>
                            </div>
                            <div className="flex items-center justify-between py-2.5">
                                <div className="flex items-center gap-2">
                                    <MapPin className="w-4 h-4 text-muted-foreground" />
                                    <span className="text-sm">Személyes átvétel</span>
                                </div>
                                <span className="text-sm font-mono text-green-500">Ingyenes</span>
                            </div>
                        </div>
                        <p className="text-xs text-muted-foreground mt-3 pt-3 border-t border-border">
                            100 000 Ft felett ingyenes házhozszállítás
                        </p>
                    </div>

                    {/* Column 2: Fontos tudnivalók */}
                    <div className="bg-card rounded-xl border border-border p-6" data-testid="disclaimers">
                        <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                            <Shield className="w-5 h-5 text-primary" />
                            Fontos tudnivalók
                        </h3>
                        <div className="space-y-3">
                            <div className="flex gap-3">
                                <Camera className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                                <div>
                                    <p className="text-sm font-medium">Termékkép</p>
                                    <p className="text-xs text-muted-foreground">A termékképek illusztrációk, a tényleges termék megjelenése eltérhet.</p>
                                </div>
                            </div>
                            <div className="flex gap-3">
                                <Settings className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                                <div>
                                    <p className="text-sm font-medium">Termékleírás</p>
                                    <p className="text-xs text-muted-foreground">A termékleírások és specifikációk tájékoztató jellegűek.</p>
                                </div>
                            </div>
                            <div className="flex gap-3">
                                <RefreshCw className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                                <div>
                                    <p className="text-sm font-medium">Ár</p>
                                    <p className="text-xs text-muted-foreground">Az árak tájékoztató jellegűek, az árváltozás jogát fenntartjuk.</p>
                                </div>
                            </div>
                            <div className="flex gap-3">
                                <Package className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                                <div>
                                    <p className="text-sm font-medium">Készlet</p>
                                    <p className="text-xs text-muted-foreground">A készletinformációk tájékoztató jellegűek.</p>
                                </div>
                            </div>
                            <div className="flex gap-3">
                                <Clock className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                                <div>
                                    <p className="text-sm font-medium">Szállítási idő</p>
                                    <p className="text-xs text-muted-foreground">A feltüntetett szállítási idők tájékoztató jellegűek.</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Column 3: Termék Információ */}
                    <div className="bg-card rounded-xl border border-border p-6" data-testid="product-info-box">
                        <h3 className="font-heading text-lg font-bold mb-4 uppercase tracking-wide">Termék Információ</h3>
                        <div className="space-y-0">
                            <div className="flex justify-between py-2.5 border-b border-border">
                                <span className="text-muted-foreground text-sm">Márka</span>
                                <span className="text-sm font-medium">{product.brand || 'N/A'}</span>
                            </div>
                            <div className="flex justify-between py-2.5 border-b border-border">
                                <span className="text-muted-foreground text-sm">Kategória</span>
                                <span className="text-sm font-medium capitalize">{product.subcategory?.replace(/_/g, ' ') || product.category?.replace(/_/g, ' ')}</span>
                            </div>
                            <div className="flex justify-between py-2.5 border-b border-border">
                                <span className="text-muted-foreground text-sm">Állapot</span>
                                <span className="text-sm font-medium">{product.condition === 'new' ? 'Új' : 'Felújított'}</span>
                            </div>
                            <div className="flex justify-between py-2.5 border-b border-border">
                                <span className="text-muted-foreground text-sm">Garancia</span>
                                <span className="text-sm font-medium">{product.condition === 'new' ? '24 hónap' : '12 hónap'}</span>
                            </div>
                            <div className="flex justify-between py-2.5">
                                <span className="text-muted-foreground text-sm">Cikkszám</span>
                                <span className="text-sm font-mono">{product.id?.slice(0, 8).toUpperCase()}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Reviews Section */}
                <div className="mt-12 lg:mt-16">
                    <ReviewSection productId={product.id} />
                </div>

                {/* Related/Compatible Products Section */}
                {relatedProducts.length > 0 && (
                    <div className="mt-12 lg:mt-16 bg-secondary/30 -mx-4 px-4 py-6 lg:mx-0 lg:px-0 lg:py-0 lg:bg-transparent" data-testid="related-products-section">
                        <div className="flex items-center gap-3 mb-2">
                            <Puzzle className="w-5 h-5 lg:w-6 lg:h-6 text-primary" />
                            <h2 className="font-heading text-lg lg:text-xl font-bold">Kapcsolódó termékek</h2>
                        </div>
                        <div className="flex items-center justify-between mb-4">
                            <p className="text-xs lg:text-sm text-muted-foreground">
                                {product.subcategory === 'cpu' && 'Kompatibilis alaplapok és hűtők ehhez a processzorhoz'}
                                {product.subcategory === 'motherboard' && 'Kompatibilis processzorok és memóriák ehhez az alaplaphoz'}
                                {product.subcategory === 'gpu' && 'Ajánlott tápegységek és számítógépházak ehhez a videokártyához'}
                                {product.subcategory === 'ram' && 'Kompatibilis alaplapok ehhez a memóriához'}
                                {product.subcategory === 'psu' && 'Videokártyák és számítógépházak ehhez a tápegységhez'}
                                {product.subcategory === 'case' && 'Alaplapok és hűtők ehhez a házhoz'}
                                {product.subcategory === 'cooling' && 'Processzorok és számítógépházak ehhez a hűtőhöz'}
                                {product.subcategory === 'storage' && 'Alaplapok és számítógépházak ehhez a tárhelyhez'}
                                {!['cpu', 'motherboard', 'gpu', 'ram', 'psu', 'case', 'cooling', 'storage'].includes(product.subcategory) && 'Hasonló termékek, amelyek érdekelhetnek'}
                            </p>
                        </div>
                        {/* Single row horizontal scroll with arrows */}
                        <div className="relative group/scroll">
                            <button
                                onClick={function() {
                                    var container = document.getElementById('related-scroll');
                                    if (container) container.scrollBy({ left: -400, behavior: 'smooth' });
                                }}
                                className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-background/90 border border-border rounded-full flex items-center justify-center shadow-lg opacity-0 group-hover/scroll:opacity-100 transition-opacity hover:bg-primary hover:text-primary-foreground -ml-2 lg:-ml-5"
                                aria-label="Scroll left"
                            >
                                <ChevronLeft className="w-5 h-5" />
                            </button>
                            <div className="overflow-hidden -mx-4 lg:mx-0">
                                <div id="related-scroll" className="flex gap-3 overflow-x-auto pb-4 px-4 lg:px-0 scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
                                    {relatedProducts.map(function(relProduct) {
                                        return (
                                            <div key={relProduct.id} className="flex-none w-[160px] lg:w-[180px]">
                                                <ProductCard product={relProduct} compact />
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                            <button
                                onClick={function() {
                                    var container = document.getElementById('related-scroll');
                                    if (container) container.scrollBy({ left: 400, behavior: 'smooth' });
                                }}
                                className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-background/90 border border-border rounded-full flex items-center justify-center shadow-lg opacity-0 group-hover/scroll:opacity-100 transition-opacity hover:bg-primary hover:text-primary-foreground -mr-2 lg:-mr-5"
                                aria-label="Scroll right"
                            >
                                <ChevronRight className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                )}

                {/* Loading skeleton for related products */}
                {loadingRelated && (
                    <div className="mt-12 lg:mt-16">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-5 h-5 lg:w-6 lg:h-6 skeleton rounded" />
                            <div className="h-6 lg:h-8 skeleton rounded w-48" />
                        </div>
                        <div className="-mx-4 px-4 lg:mx-0 lg:px-0">
                            <div className="flex gap-3 overflow-x-auto pb-4">
                                {[1, 2, 3, 4, 5, 6].map(function(i) {
                                    return (
                                        <div key={i} className="flex-shrink-0 w-[160px] lg:w-[180px] bg-card border border-border rounded-lg overflow-hidden">
                                            <div className="aspect-square skeleton" />
                                            <div className="p-2 space-y-1.5">
                                                <div className="h-3 skeleton rounded w-1/3" />
                                                <div className="h-4 skeleton rounded w-full" />
                                                <div className="h-4 skeleton rounded w-1/2" />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}

                {/* Recently Viewed Section */}
                {recentlyViewed.length > 0 && (
                    <div className="mt-12 lg:mt-16 bg-secondary/30 -mx-4 px-4 py-6 lg:mx-0 lg:px-0 lg:py-0 lg:bg-transparent" data-testid="recently-viewed-section">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                                <Clock className="w-5 h-5 lg:w-6 lg:h-6 text-primary" />
                                <h2 className="font-heading text-lg lg:text-xl font-bold">Korábban megtekintett</h2>
                            </div>
                        </div>
                        {/* Single row horizontal scroll with arrows */}
                        <div className="relative group/scroll">
                            <button
                                onClick={function() {
                                    var container = document.getElementById('recent-scroll');
                                    if (container) container.scrollBy({ left: -400, behavior: 'smooth' });
                                }}
                                className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-background/90 border border-border rounded-full flex items-center justify-center shadow-lg opacity-0 group-hover/scroll:opacity-100 transition-opacity hover:bg-primary hover:text-primary-foreground -ml-2 lg:-ml-5"
                                aria-label="Scroll left"
                            >
                                <ChevronLeft className="w-5 h-5" />
                            </button>
                            <div className="overflow-hidden -mx-4 lg:mx-0">
                                <div id="recent-scroll" className="flex gap-3 overflow-x-auto pb-4 px-4 lg:px-0 scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
                                    {recentlyViewed.map(function(viewedProduct) {
                                        return (
                                            <div key={viewedProduct.id} className="flex-none w-[160px] lg:w-[180px]">
                                                <ProductCard product={viewedProduct} compact />
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                            <button
                                onClick={function() {
                                    var container = document.getElementById('recent-scroll');
                                    if (container) container.scrollBy({ left: 400, behavior: 'smooth' });
                                }}
                                className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-background/90 border border-border rounded-full flex items-center justify-center shadow-lg opacity-0 group-hover/scroll:opacity-100 transition-opacity hover:bg-primary hover:text-primary-foreground -mr-2 lg:-mr-5"
                                aria-label="Scroll right"
                            >
                                <ChevronRight className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                )}
            </div>
            
            {/* Unified Product Editor Dialog */}
            <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col mx-4 sm:mx-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-3">
                            <span className="truncate">{product?.name}</span>
                        </DialogTitle>
                        {/* Tabs */}
                        <div className="flex gap-1 border-b border-border pt-2">
                            <button
                                onClick={() => setEditorTab('details')}
                                className={cn(
                                    "px-4 py-2 text-sm font-medium rounded-t-md transition-colors",
                                    editorTab === 'details'
                                        ? "bg-primary text-primary-foreground"
                                        : "text-muted-foreground hover:text-foreground hover:bg-muted"
                                )}
                                data-testid="editor-tab-details"
                            >
                                <Pencil className="w-4 h-4 inline mr-2" />
                                Adatok
                            </button>
                            <button
                                onClick={() => { setEditorTab('images'); if (imgSearchResults.length === 0 && imgSearchQuery) doImageSearch(imgSearchQuery); }}
                                className={cn(
                                    "px-4 py-2 text-sm font-medium rounded-t-md transition-colors",
                                    editorTab === 'images'
                                        ? "bg-primary text-primary-foreground"
                                        : "text-muted-foreground hover:text-foreground hover:bg-muted"
                                )}
                                data-testid="editor-tab-images"
                            >
                                <Camera className="w-4 h-4 inline mr-2" />
                                Képek ({currentImages.length})
                            </button>
                        </div>
                    </DialogHeader>

                    <div className="flex-1 overflow-y-auto">
                        {/* Details Tab */}
                        {editorTab === 'details' && (
                            <div className="grid gap-4 py-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="edit-name">Név</Label>
                                        <Input id="edit-name" value={editForm.name || ''} onChange={function(e) { handleEditFormChange('name', e.target.value); }} />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="edit-brand">Márka</Label>
                                        <Input id="edit-brand" value={editForm.brand || ''} onChange={function(e) { handleEditFormChange('brand', e.target.value); }} />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="edit-description">Leírás</Label>
                                    <Textarea id="edit-description" value={editForm.description || ''} onChange={function(e) { handleEditFormChange('description', e.target.value); }} rows={3} />
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="edit-price">Ár (Ft)</Label>
                                        <Input id="edit-price" type="number" value={editForm.price || ''} onChange={function(e) { handleEditFormChange('price', e.target.value); }} />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="edit-original-price">Eredeti ár (Ft)</Label>
                                        <Input id="edit-original-price" type="number" value={editForm.original_price || ''} onChange={function(e) { handleEditFormChange('original_price', e.target.value); }} placeholder="Opcionális" />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="edit-stock">Készlet</Label>
                                        <Input id="edit-stock" type="number" value={editForm.stock || ''} onChange={function(e) { handleEditFormChange('stock', e.target.value); }} />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Készlet státusz</Label>
                                        <Select value={editForm.stock_status || 'raktaron'} onValueChange={function(v) { handleEditFormChange('stock_status', v); }}>
                                            <SelectTrigger><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="raktaron">Rakt&#225;ron</SelectItem>
                                                <SelectItem value="beszallitoi_raktaron">Besz&#225;ll&#237;t&#243;i rakt&#225;ron</SelectItem>
                                                <SelectItem value="rendelheto">Rendelhet&#337;</SelectItem>
                                                <SelectItem value="nincs_keszleten">Nincs k&#233;szleten</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="space-y-2">
                                        <Label>Kategória</Label>
                                        <Select value={editForm.category || ''} onValueChange={function(v) { handleEditFormChange('category', v); }}>
                                            <SelectTrigger><SelectValue placeholder="Válassz" /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="component">Alkatrész</SelectItem>
                                                <SelectItem value="gaming_pc">Gamer PC</SelectItem>
                                                <SelectItem value="office_pc">Irodai PC</SelectItem>
                                                <SelectItem value="workstation">Munkaállomás</SelectItem>
                                                <SelectItem value="mobile_phone">Mobiltelefon</SelectItem>
                                                <SelectItem value="accessory">Tartozék</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Alkategória</Label>
                                        <Select value={editForm.subcategory || ''} onValueChange={function(v) { handleEditFormChange('subcategory', v); }}>
                                            <SelectTrigger><SelectValue placeholder="Válassz" /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="cpu">Processzor</SelectItem>
                                                <SelectItem value="gpu">Videokártya</SelectItem>
                                                <SelectItem value="motherboard">Alaplap</SelectItem>
                                                <SelectItem value="ram">Memória</SelectItem>
                                                <SelectItem value="storage">Tárhely</SelectItem>
                                                <SelectItem value="psu">Tápegység</SelectItem>
                                                <SelectItem value="case">Ház</SelectItem>
                                                <SelectItem value="cooling">Hűtés</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Állapot</Label>
                                        <Select value={editForm.condition || ''} onValueChange={function(v) { handleEditFormChange('condition', v); }}>
                                            <SelectTrigger><SelectValue placeholder="Válassz" /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="new">Új</SelectItem>
                                                <SelectItem value="refurbished">Felújított</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                <div className="flex items-center gap-6 pt-2">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input type="checkbox" checked={editForm.featured || false} onChange={function(e) { handleEditFormChange('featured', e.target.checked); }} className="w-4 h-4 rounded border-border" />
                                        <span className="text-sm">Kiemelt termék</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input type="checkbox" checked={editForm.visible !== false} onChange={function(e) { handleEditFormChange('visible', e.target.checked); }} className="w-4 h-4 rounded border-border" />
                                        <span className="text-sm">Látható</span>
                                    </label>
                                </div>
                                <div className="flex justify-end gap-3 pt-4 border-t border-border">
                                    <Button variant="outline" onClick={function() { setEditorOpen(false); }} disabled={saving}>
                                        <X className="w-4 h-4 mr-2" />Mégse
                                    </Button>
                                    <Button onClick={handleSaveProduct} disabled={saving}>
                                        <Save className="w-4 h-4 mr-2" />{saving ? 'Mentés...' : 'Mentés'}
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* Images Tab */}
                        {editorTab === 'images' && (
                            <div className="space-y-4 py-4">
                                {/* Current Images */}
                                <div className="bg-secondary/50 rounded-lg p-3">
                                    <p className="text-xs font-medium text-muted-foreground mb-2" data-testid="current-images-count">
                                        Jelenlegi képek ({currentImages.length})
                                    </p>
                                    {currentImages.length > 0 ? (
                                        <div className="flex gap-2 overflow-x-auto pb-1">
                                            {currentImages.map(function(img, i) {
                                                return (
                                                    <div key={'cur-' + i} className="relative flex-shrink-0 group">
                                                        <img src={proxyUrl(img)} alt="" className="w-16 h-16 rounded object-cover border border-border"
                                                            onError={function(e) { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64"><rect fill="%23333" width="64" height="64"/><text fill="%23666" x="18" y="36" font-size="12">?</text></svg>'; }} />
                                                        <button onClick={function() { handleRemoveImage(i); }}
                                                            className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                                            data-testid={'remove-current-image-' + i}>
                                                            <X className="w-3 h-3" />
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <p className="text-xs text-red-500">Nincsenek képek</p>
                                    )}
                                </div>

                                {/* Search Bar */}
                                <div className="flex gap-2">
                                    <Input value={imgSearchQuery} onChange={function(e) { setImgSearchQuery(e.target.value); }}
                                        onKeyDown={function(e) { if (e.key === 'Enter') doImageSearch(imgSearchQuery); }}
                                        placeholder="Keresési kifejezés..." data-testid="img-search-input" />
                                    <Button onClick={function() { doImageSearch(imgSearchQuery); }} disabled={imgSearching} data-testid="img-search-btn">
                                        {imgSearching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                                    </Button>
                                </div>

                                {/* Search Results */}
                                {imgSearching ? (
                                    <div className="text-center py-8">
                                        <Loader2 className="w-8 h-8 mx-auto animate-spin text-cyan-500" />
                                        <p className="text-sm text-muted-foreground mt-2">Képek keresése...</p>
                                    </div>
                                ) : imgSearchResults.length > 0 ? (
                                    <>
                                        <div className="flex items-center justify-between">
                                            <p className="text-xs text-muted-foreground">
                                                {imgSearchResults.length} találat
                                                {imgSelected.size > 0 && (
                                                    <span className="text-cyan-500 font-medium ml-2">{imgSelected.size} kiválasztva</span>
                                                )}
                                            </p>
                                            {imgSelected.size > 0 && (
                                                <button onClick={function() { setImgSelected(new Set()); }} className="text-xs text-muted-foreground hover:text-foreground">
                                                    Kiválasztás törlése
                                                </button>
                                            )}
                                        </div>
                                        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2" data-testid="img-search-results">
                                            {imgSearchResults.map(function(img, i) {
                                                var isSelected = imgSelected.has(img.url);
                                                var alreadyAdded = currentImages.includes(img.url);
                                                return (
                                                    <div key={i} data-testid={'img-result-' + i}
                                                        onClick={function() { if (!alreadyAdded) toggleImgSelect(img.url); }}
                                                        className={cn(
                                                            "relative aspect-square rounded-lg overflow-hidden cursor-pointer border-2 transition-all",
                                                            alreadyAdded ? "border-green-500/50 opacity-60 cursor-not-allowed"
                                                            : isSelected ? "border-cyan-500 ring-2 ring-cyan-500/30 scale-[1.02]"
                                                            : "border-transparent hover:border-border"
                                                        )}>
                                                        <img src={proxyUrl(img.thumbnail || img.url)} alt={img.title || ''} className="w-full h-full object-cover"
                                                            onError={function(e) { e.target.onerror = null; e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200"><rect fill="%23222" width="200" height="200"/><text fill="%23555" x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-size="14">Nem tölthető</text></svg>'; }} />
                                                        {isSelected && (
                                                            <div className="absolute inset-0 bg-cyan-500/20 flex items-center justify-center">
                                                                <div className="w-7 h-7 rounded-full bg-cyan-500 flex items-center justify-center">
                                                                    <Check className="w-4 h-4 text-white" />
                                                                </div>
                                                            </div>
                                                        )}
                                                        {alreadyAdded && (
                                                            <div className="absolute bottom-0 inset-x-0 bg-green-500/90 text-white text-[10px] text-center py-0.5">Hozzáadva</div>
                                                        )}
                                                        <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/60 to-transparent p-1">
                                                            <p className="text-[9px] text-white/70 truncate">{img.source}</p>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </>
                                ) : !imgSearching && imgSearchQuery && (
                                    <div className="text-center py-8 text-muted-foreground">
                                        <ImageIcon className="w-10 h-10 mx-auto mb-2 opacity-40" />
                                        <p className="text-sm">Keress képeket a termékhez</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Image action bar (only on images tab with selections) */}
                    {editorTab === 'images' && (
                        <div className="border-t border-border pt-3 flex flex-col sm:flex-row gap-2">
                            {imgSelected.size > 0 ? (
                                <>
                                    <Button onClick={handleReplaceImages} disabled={saving} className="flex-1 bg-cyan-600 hover:bg-cyan-700" data-testid="img-replace-btn">
                                        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Replace className="w-4 h-4 mr-2" />}
                                        Csere ({imgSelected.size} kép)
                                    </Button>
                                    <Button onClick={handleAddImages} disabled={saving} variant="outline" className="flex-1" data-testid="img-add-btn">
                                        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                                        Hozzáadás ({imgSelected.size})
                                    </Button>
                                </>
                            ) : (
                                <p className="text-sm text-muted-foreground text-center flex-1 py-2">Válassz ki képeket a mentéshez</p>
                            )}
                            <Button variant="ghost" onClick={function() { setEditorOpen(false); }} data-testid="editor-close-btn">Bezárás</Button>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
